import React, { useState } from 'react';
import { ChevronDown, Table as TableIcon, Download, FileSpreadsheet } from 'lucide-react';
import { formatMoney } from '../lib/currency';
import { monthsToLabel } from '../mock';
import { useAppSettings } from '../lib/i18n';

export default function AmortizationTable({ schedule, monthsTaken, onDownloadPdf, onDownloadCsv }) {
  const { t, currency } = useAppSettings();
  const [open, setOpen] = useState(false);

  const rows = schedule.map((r) => ({ ...r, year: Math.ceil(r.month / 12) }));

  return (
    <section className="emi-wrap mt-8">
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="w-full flex items-center justify-between p-5 gap-3">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex items-center gap-3 flex-1 text-left hover:opacity-80 transition-opacity"
          >
            <div className="h-10 w-10 rounded-lg bg-[#FDF2F8] text-[#DB2777] flex items-center justify-center">
              <TableIcon className="h-5 w-5" />
            </div>
            <div>
              <div className="text-base font-semibold text-[#032D60]">{t('amortization_title')}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {t('amortization_desc')} · {schedule.length} {t('rows')} · {t('payoff_in')} {monthsToLabel(monthsTaken)}
              </div>
            </div>
          </button>

          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={onDownloadCsv}
              className="btn-3d inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-gradient-to-b from-[#10B981] to-[#059669] hover:from-[#34D399] hover:to-[#047857] text-white text-xs font-semibold"
              data-testid="download-csv-btn"
              title="Download amortization schedule as CSV"
            >
              <FileSpreadsheet className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{t('download_csv')}</span>
            </button>
            <button
              onClick={onDownloadPdf}
              className="btn-3d inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-gradient-to-b from-[#EC4899] to-[#DB2777] hover:from-[#F472B6] hover:to-[#BE185D] text-white text-xs font-semibold"
              data-testid="download-pdf-btn"
            >
              <Download className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{t('download_pdf')}</span>
            </button>
            <button
              onClick={() => setOpen((v) => !v)}
              className="h-9 w-9 rounded-md hover:bg-slate-100 flex items-center justify-center"
              aria-label="toggle"
            >
              <ChevronDown className={`h-5 w-5 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>

        <div className="transition-all overflow-hidden" style={{ maxHeight: open ? '560px' : '0px' }}>
          <div className="border-t border-slate-100 max-h-[520px] overflow-y-auto">
            <table className="w-full text-xs sm:text-sm">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr className="text-slate-600">
                  <Th>{t('month')}</Th>
                  <Th>{t('phase')}</Th>
                  <Th align="right">{t('payment')}</Th>
                  <Th align="right">{t('principal')}</Th>
                  <Th align="right">{t('total_interest')}</Th>
                  <Th align="right">{t('prepay')}</Th>
                  <Th align="right">{t('balance')}</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={r.month}
                    className={`border-t border-slate-100 ${
                      r.phase.startsWith('Moratorium')
                        ? 'bg-[#FEF9C3]/40'
                        : r.prepayment > 0
                        ? 'bg-[#ECFDF5]/60'
                        : i % 2
                        ? 'bg-white'
                        : 'bg-slate-50/40'
                    }`}
                  >
                    <Td>{r.month}</Td>
                    <Td>
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium ${
                          r.phase.startsWith('Moratorium')
                            ? 'bg-[#FEF3C7] text-[#B45309]'
                            : 'bg-[#EAF5FE] text-[#0176D3]'
                        }`}
                      >
                        {r.phase.startsWith('Moratorium')
                          ? r.phase.includes('Int')
                            ? `${t('moratorium')} (${t('total_interest')})`
                            : t('moratorium')
                          : t('emi_phase')}
                      </span>
                    </Td>
                    <Td align="right">{formatMoney(r.emi, currency)}</Td>
                    <Td align="right">{formatMoney(r.principalPaid, currency)}</Td>
                    <Td align="right">{formatMoney(r.interestPaid, currency)}</Td>
                    <Td align="right" className={r.prepayment ? 'text-[#059669] font-semibold' : 'text-slate-400'}>
                      {r.prepayment ? formatMoney(r.prepayment, currency) : '\u2014'}
                    </Td>
                    <Td align="right" className="font-medium">{formatMoney(r.balance, currency)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

function Th({ children, align = 'left' }) {
  return <th className={`px-3 py-2.5 text-[11px] uppercase tracking-wide font-semibold ${align === 'right' ? 'text-right' : 'text-left'}`}>{children}</th>;
}
function Td({ children, align = 'left', className = '' }) {
  return <td className={`px-3 py-2 tabular-nums text-slate-700 ${align === 'right' ? 'text-right' : 'text-left'} ${className}`}>{children}</td>;
}
